import React, { Component } from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { H4, P, Btn } from '../../AbstractElements';

/**
 * ErrorBoundary - Catches JavaScript errors anywhere in the child component tree
 * Logs those errors and displays a fallback UI instead of crashing
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Optionally reload the page or navigate to a safe route
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom error fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Container fluid={true} className="p-4">
          <Row className="justify-content-center">
            <Col md="8" lg="6">
              <Card className="shadow-sm">
                <CardBody className="text-center p-5">
                  <div className="mb-4">
                    <i className="icofont icofont-warning text-danger" style={{ fontSize: '4rem' }}></i>
                  </div>
                  <H4 className="text-danger mb-3">Oops! Something went wrong</H4>
                  <P className="text-muted mb-4">
                    We're sorry for the inconvenience. The application encountered an unexpected error.
                  </P>

                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <div className="text-left mb-4">
                      <details className="error-details">
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                          Error Details (Development Only)
                        </summary>
                        <div className="mt-3 p-3 bg-light rounded">
                          <p className="text-danger mb-2">
                            <strong>Error:</strong> {this.state.error.toString()}
                          </p>
                          {this.state.errorInfo && (
                            <pre className="text-muted small" style={{ maxHeight: '200px', overflow: 'auto' }}>
                              {this.state.errorInfo.componentStack}
                            </pre>
                          )}
                        </div>
                      </details>
                    </div>
                  )}

                  <div className="d-flex gap-2 justify-content-center">
                    <Btn
                      attrBtn={{
                        color: 'primary',
                        onClick: this.handleReset,
                      }}
                    >
                      Try Again
                    </Btn>
                    <Btn
                      attrBtn={{
                        color: 'secondary',
                        outline: true,
                        onClick: () => window.location.href = '/',
                      }}
                    >
                      Go to Homepage
                    </Btn>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
